import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { ModalService } from '../../../../../../../components/modal-list/modal-list.service';
import { CommonModule } from '@angular/common';
import { SharedSelectTableConfirmationComponent } from './close-shared-selection-table-confirmation/close-confirmation.component';
import { DataTableService } from './dataTable.service';
import { FormsModule } from '@angular/forms';
import { CustomPaginationModule } from '../../../../../misc/pagination/pagination.module';
import { DEVICE_SIZE } from '../../../../../../../core/models/others.model';
import { ScreenViewModule } from '../../../../../../../core/directives/screen-view/_screen-view.module';
import { I18nModule } from '@spartacus/core';
import { LocalStorageService } from '../../../../../../../core/services/localStorage.service';
import { TableProComponent } from '../../../../../../../components/table-pro/table-pro.component';
import { TableProConfig } from '../../../../../../../components/table-pro/core/table-pro.model';

@Component({
  selector: 'shared-selection-table',
  standalone: true,
  imports: [CommonModule, FormsModule, CustomPaginationModule, ScreenViewModule, I18nModule, TableProComponent],
  templateUrl: './shared-selection-table.component.html',
  styleUrls: ['./shared-selection-table.component.scss'],
})
export class SharedSelectionTableComponent<T extends { uid: string }> implements OnInit {
  @Input() from: 'sold-to' | 'ship-to' | '' = '';
  @Input() items: T[] = [];
  @Input() columns: { key: keyof T; label: string; keyString: string }[] = [];
  @Input() columnsStrings: { key: String; label: string }[] = [];
  @Input() jsonItemName: string = '';
  @Input() tableTitle: string = '';
  @Input() tableText: string = '';
  @Output() selectedItemOutput = new EventEmitter<T>();

  selectedItem: T | null = null;
  searchTerm: string = '';
  sortField: keyof T | null = null;
  sortOrder: 'asc' | 'desc' | null = null;
  sortValue: string = 'none';
  pageSize: number = 10;
  totalPages: number = Math.ceil(this.items.length / this.pageSize);
  currentPage: number = 1;
  checkedValue: string = '';
  sortOptions = this.getSortOptions();
  DEVICE_SIZE = DEVICE_SIZE;
  preSelectedItem: T | null = null;

  tableConfig: TableProConfig = {
    classContainer: 'm-t-20',
    collumnConfig: [],
    hasItemsPerPageSelector: false,
    itemsPerPage: 10,
    itemsPerPageConfig: [10, 15, 20, 50],
  };

  constructor(
    private modalService: ModalService,
    private dataTableService: DataTableService<T>,
    private localStorageService: LocalStorageService,
  ) {}

  ngOnInit() {
    this.setTableConfig();

    this.sortOptions = this.getSortOptions();
    const storedItem = this.localStorageService.getItem(this.jsonItemName);

    if (storedItem) {
      this.selectedItem = JSON.parse(storedItem);
      this.onSelectItem(JSON.parse(storedItem));
    }
  }

  setTableConfig() {
    if (this.from === 'ship-to') {
      this.tableConfig.collumnConfig = [
        { id: 'select', index: 0, type: 'text', isOrdenable: false },
        { id: 'street', index: 1, type: 'text' },
        { id: 'city', index: 2, type: 'text' },
        { id: 'state', index: 3, type: 'text' },
        { id: 'postalCode', index: 4, type: 'text' },
        { id: 'country', index: 5, type: 'text' },
        { id: 'location', index: 6, type: 'text' },
        { id: 'defaultWareHouse', index: 7, type: 'text' },
      ];
    }

    if (this.from === 'sold-to') {
      this.tableConfig.collumnConfig = [
        { id: 'select', index: 0, type: 'text', isOrdenable: false },
        { id: 'accountNumber', index: 1, type: 'text' },
        { id: 'name', index: 2, type: 'text' },
        { id: 'address', index: 3, type: 'text' },
      ];
    }
  }

  skipModal() {
    this.modalService.create({
      id: 'shared-selection-table-close-confirmation',
      title: '',
      width: 500,
      hasCloseButton: false,
      component: SharedSelectTableConfirmationComponent,
    });
  }

  closeModal() {
    this.modalService.close();
  }

  onSelectItem(item: T) {
    this.selectedItem = item;
    this.selectedItemOutput.emit(item);
  }

  get filteredAndPaginatedItems(): T[] {
    return this.items;

    // const filtered = this.dataTableService.filteredItems(
    //   this.items,
    //   this.searchTerm,
    //   this.columns.map(column => column.key),
    //   this.sortField,
    //   this.sortOrder,
    // );

    // if (filtered !== this.items) {
    //   return this.dataTableService.paginateItems(filtered, 1, this.pageSize);
    // }

    // return this.dataTableService.paginateItems(filtered, this.currentPage, this.pageSize);
  }

  sortTable(field: keyof T) {
    const sortResults = this.dataTableService.sortTable(this.sortField, field, this.sortOrder);
    this.sortField = sortResults.sortField;
    this.sortOrder = sortResults.sortOrder;
  }

  getSortIcon(field: keyof T) {
    return this.dataTableService.getSortIcon(this.sortField, field, this.sortOrder);
  }

  nextPage() {
    this.currentPage++;
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  setPage(page: number) {
    this.currentPage = page;
  }

  onSortChange(selectedValue: string) {
    this.sortValue = selectedValue;

    if (selectedValue === 'none') {
      this.sortField = null;
      this.sortOrder = null;
      return this.dataTableService.paginateItems(this.items, 1, this.pageSize);
    } else {
      const [field, order] = selectedValue.split('-') as [keyof T, 'asc' | 'desc'];
      this.sortField = field;
      this.sortOrder = order;
      return;
    }
  }

  getPages(): number[] {
    const pages: number[] = [];
    this.totalPages = Math.ceil(this.items?.length / this.pageSize);

    let start = Math.max(1, this.currentPage - 1);
    let end = Math.min(this.totalPages, this.currentPage + 1);

    if (this.currentPage === 1) {
      end = Math.min(3, this.totalPages);
    }

    if (this.currentPage === this.totalPages) {
      start = Math.max(1, this.totalPages - 2);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  goToPage(page: number): void {
    this.currentPage = page;
  }

  getSortOptions() {
    const options = [{ label: 'Sort By', value: 'none' }];

    this.columns.forEach(column => {
      options.push({ label: `${column.label} (asc)`, value: `${column.keyString}-asc` });
      options.push({ label: `${column.label} (desc)`, value: `${column.keyString}-desc` });
    });

    return options;
  }
}
